import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Button,
  Text,
  VStack,
  Image,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import toolboxIcon from '../Assets/icons/toolboxIcon.svg';
import cartIcon from '../Assets/icons/cartIcon.svg';
import phoneIcon from '../Assets/icons/phoneIcon.svg';
import Header from '../Components/Header';
import { clearToken } from '../utils/tokenManager';

const ServicesPage = () => {
  const [hasToken, setHasToken] = useState(false);
  const breakpointMobile = useBreakpointValue({ base: true, lg: false });
  
  // Fallback to window width check for more reliable mobile detection
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024; // lg breakpoint is typically 1024px
    }
    return breakpointMobile ?? true;
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    setHasToken(!!token);
  }, []);

  // Update mobile state when breakpoint changes or window resizes
  useEffect(() => {
    const updateMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024);
    };

    // Use breakpoint value if available, otherwise use window width
    if (breakpointMobile !== undefined) {
      setIsMobile(breakpointMobile);
    } else {
      updateMobile();
    }

    window.addEventListener('resize', updateMobile);
    return () => window.removeEventListener('resize', updateMobile);
  }, [breakpointMobile]);

  const handleMenuClick = () => {
    console.log('Menu clicked');
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('username');
    setHasToken(false);
  };

  const handleServiceClick = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Mobile: navigate to separate page
      // Desktop: use existing modal flow
      // Check if mobile (isMobile === true, not undefined or false)
      if (isMobile === true) {
        console.log('Mobile detected, navigating to /vehicle-details');
        navigate('/vehicle-details');
      } else {
        // Desktop: keep existing modal flow
        console.log('Desktop detected, using modal flow');
        localStorage.setItem('openVehicleForm', 'true');
        navigate('/dashboard');
      }
      return;
    }
    navigate('/dashboard');
  };

  const services = [
    {
      icon: <Image src={toolboxIcon} alt="Diagnose Repair Vehicle" w="6" h="6" />,
      text: 'Diagnose Repair Vehicle',
      bgColor: '#e3cff3',
      onClick: handleServiceClick,
    },
    {
      icon: <Image src={cartIcon} alt="Vehicle Value" w="6" h="6" />,
      text: 'Vehicle Value',
      bgColor: '#d1efec',
      onClick: handleServiceClick,
    },
    {
      icon: <Image src={phoneIcon} alt="Help Expert" w="6" h="6" />,
      text: 'Help Expert',
      bgColor: '#f6dade',
      onClick: handleServiceClick,
    },
  ];

  return (
    <Box
      bg="white"
      minH="100vh"
      style={{
        WebkitTextSizeAdjust: '100%',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <Header
        hasToken={hasToken}
        onMenuClick={handleMenuClick}
        onLogout={handleLogout}
        isChatView={false}
      />

    {/* <Flex justifyContent="center" px={{ base: 0, md: 6 }} pt={0}> */}
      <Flex 
        justifyContent="center" 
        px={{ base: 0, md: 6 }} 
        pt={{ base: '2px', md: '68px' }}
      >
        <Box
          w="full"
          maxW={{ base: '100%', md: '480px' }}
          overflow="hidden"
          borderRadius={{ base: '0', md: 'md' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          h={{ base: '200px', md: '180px' }}
          bg="#212e89"
          px={4}
        >
          <Text
            fontSize={{ base: 'xl', md: '2xl' }}
            fontWeight="bold"
            textAlign="center"
            lineHeight="1.4"
            fontFamily="Poppins, sans-serif"
          >
            <Text as="span" color="white" fontFamily="Poppins, sans-serif">
              Here to help you in your moment of{' '}
            </Text>
            <Text as="span" color="#00E676" fontFamily="Poppins, sans-serif">
              automotive crisis
            </Text>
          </Text>
        </Box>
      </Flex>

      <Flex alignItems="center" justifyContent="center" px={6} py={4}>
        <VStack spacing={6} maxW="400px" w="full">
          {services.map((service) => (
            <Button
              key={service.text}
              onClick={service.onClick}
              bg={service.bgColor}
              color="gray.700"
              borderRadius="full"
              border="none"
              px={6}
              py={3}
              w="full"
              h="64px"
              minH="50px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
                bg: service.bgColor,
              }}
              _active={{
                transform: 'translateY(0px)',
                bg: service.bgColor,
              }}
              transition="all 0.2s ease-in-out"
              cursor="pointer"
            >
              <Box>{service.icon}</Box>
              <Text
                fontSize="md"
                fontWeight="semibold"
                textAlign="center"
                color="gray.700"
              >
                {service.text}
              </Text>
            </Button>
          ))}

          <Box h={2} />

          <Button
            color="white"
            bg="#3182ce"
            _hover={{ bg: '#2b6cb0' }}
            w="full"
            h="44px"
            borderRadius="8px"
            onClick={() => navigate('/sign-in')}
          >
            Login
          </Button>
          <Button
            variant="outline"
            colorScheme="blue"
            w="full"
            h="44px"
            borderRadius="8px"
            onClick={() => navigate('/sign-up')}
          >
            Sign Up
          </Button>
        </VStack>
      </Flex>
    </Box>
  );
};

export default ServicesPage;

